<template>
  <content-dialog
    v-model="opened"
    fullscreen-tablet
    card-class="staff-card"
    width="750px"
    min-height="50vh"
    without-blur
    :toolbar-classes="{'text-white': true, 'bg-blue-8': !isArchived, 'bg-red-8': isArchived}"
    :loading="loading"
    :tags="[helpCodes.staff]"
    @hide="close"
  >
    <template #toolbar>
      <q-toolbar>
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="mdi-close"
        />
        <page-toolbar-title>
          <template v-if="isArchived || (!loading && !isAllowedEditing)" #subtitle>
            {{ concatStrIf(' | ', isArchived && 'Archived', !isAllowedEditing && 'Read-only') }}
          </template>
          Staff
        </page-toolbar-title>
        <help-button />
      </q-toolbar>
      <q-tabs
        v-if="!!user"
        v-model="selectedTab"
        inline-label
        dense
        no-caps
        active-color="white"
        align="left"
        narrow-indicator
      >
        <q-tab
          name="details"
          label="Details"
        />
        <q-tab
          v-if="can(permissions.canManageUsers)"
          name="licences"
          label="Licences"
        />
        <q-tab
          v-if="can(permissions.canManageUsers)"
          name="activity"
          label="Activity Log"
        />
      </q-tabs>
    </template>

    <div v-if="!loading && !!user">
      <q-tab-panels v-model="selectedTab" class="overflow-panels">
        <q-tab-panel
          name="details"
          class="q-pa-none"
        >
          <DetailsPanel
            :user="user"
            :timeline="timeline"
            :can-edit="isAllowedEditing"
            :is-archived="isArchived"
          />
        </q-tab-panel>
        <q-tab-panel
          v-if="can(permissions.canManageUsers)"
          name="licences"
          class="q-pa-none"
        >
          <LicencesPanel
            :id="user.id"
            :licences="licences"
            :read-only="isArchived || !isAllowedEditing"
            @reload="loadUser"
          />
        </q-tab-panel>
        <q-tab-panel
          v-if="can(permissions.canManageUsers)"
          name="activity"
          class="q-pa-none"
        >
          <LogsPanel
            :id="user.id"
          />
        </q-tab-panel>
      </q-tab-panels>
    </div>
    <div v-else-if="!loading">
      <no-results
        icon="mdi-account-off"
        title="User could not be found"
      />
    </div>

    <template #bottom>
      <q-toolbar v-if="user && selectedTab == 'details'">
        <q-space />
        <q-btn
          v-if="!user.created_company && userData.id !== user.id && isAllowedArchiving"
          icon-right="mdi-package-down"
          flat
          :label="isArchived ? 'Restore' : 'Deactivate'"
          :color="isArchived ? 'teal' : 'red-7'"
          @click="deleteUser"
        />
        <q-btn
          v-if="!isArchived && isAllowedEditing"
          icon-right="mdi-pencil"
          label="Edit"
          class="q-ml-sm"
          color="blue-grey"
          @click="$refs.editDialog.open(user)"
        />
      </q-toolbar>
    </template>
  </content-dialog>

  <EditDialog
    ref="editDialog"
    @saved="loadUser(true)"
  />
</template>

<script>
import DetailsPanel from './panels/Details'
import LicencesPanel from './panels/Licences'
import LogsPanel from './panels/Logs'

import EditDialog from './../edit/Dialog'

import { concatStrIf } from 'src/helpers/globals'

export default {
  components: {
    DetailsPanel, LicencesPanel, EditDialog, LogsPanel
  },
  props: {
    id: {
      type: Number,
      default: null
    },
    allowEditing: {
      type: Boolean,
      default: false
    },
    allowArchiving: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'updated', 'archived'],
  data () {
    return {
      opened: false,
      loading: false,
      user: null,
      licences: [],
      timeline: [],
      errors: {},
      selectedTab: 'details'
    }
  },
  computed: {
    isAllowedEditing () {
      return this.can(this.permissions.canManageUsers) && this.allowEditing && !this.isArchived
    },
    isAllowedArchiving () {
      return this.allowArchiving && this.can(this.permissions.canManageUsers)
    },
    isArchived () {
      return this.user && this.user.deactivated
    }
  },
  watch: {
    id () {
      this.loadUser()
    }
  },
  methods: {
    concatStrIf,
    close () {
      this.$emit('close')
      this.selectedTab = 'details'
    },
    async loadUser (savedEdit = false) {
      if (this.id === null) { return }
      this.opened = true
      this.user = null
      this.licences = []
      this.timeline = []
      this.loading = true

      const { status, payload } = await this.$get('v1/business/company/users', {
        user_id: this.id
      })
      this.loading = false
      if (status === 'success') {
        if (payload.user.permissions) {
          payload.user.permissions = payload.user.permissions.map(item => item.name)
        }
        payload.user.roles = payload.user.roles.map(item => item.name)
        this.user = payload.user
        this.licences = payload.licences
        this.timeline = payload.timeline

        if (savedEdit) {
          this.$emit('updated', payload.user)
        }
      }
    },
    deleteUser () {
      this.$q.dialog({
        title: 'Confirm',
        message: [
          `Are you sure you want to ${this.isArchived ? 'restore' : 'deactivate'} this staff account?`,
          !this.isArchived && 'Some documents may not display this user\'s details unless they are restored'
        ],
        ok: true,
        cancel: true,
        color: 'orange-7'
      })
        .onOk(async () => {
          this.$loading.show()
          const { status, message } = await this.$remove('v1/business/company/users', {
            user_id: this.user.id
          })
          this.$loading.hide()
          if (status === 'success') {
            this.loadUser()

            if (this.isArchived) {
              this.$q.notify({
                color: 'orange-6',
                textColor: 'black',
                message: 'User successfully archived'
              })
            }
            else {
              this.$q.notify({
                color: 'green',
                message: 'User successfully restored'
              })
            }
            this.$bus.emit('staff:reload', true, true)
            this.$emit('archived', this.isArchived)
          }
          else {
            this.$q.dialog({
              title: 'Error',
              message
            })
          }
        })
    }
  },
  mounted () {
    this.loadUser()
  }
}
</script>
